import { Routes } from '@angular/router';

import { environment } from '@environments/environment';

import { ROUTE_PATHS } from '@configs/route-paths.const';

import { appConfigGuard } from '@guards/appConfig.guard';
import { HomeRedirectComponent } from '@guards/home-route.component';
import { loggedGuard } from '@guards/logged.guard';
import { moduleGuard } from '@guards/module.guard';
import { operationGuard } from '@guards/operation.guard';
import { unloggedGuard } from '@guards/unlogged.guard';

import { DashboardLayout } from '@layouts/dashboard/dashboard.component';
import { WelcomeLayout } from '@layouts/welcome/welcome.component';

import { loadRemoteModule } from '@angular-architects/module-federation';

import { ProfilePage } from '@pages/account/profile/profile.component';
import { CompaniesLoaderComponent } from '@pages/dashboard/companies-loader/companies-loader.component';
import { DataStudioLoaderComponent } from '@pages/dashboard/data-studio-loader/data-studio-loader.component';
import { FinancialsLoaderComponent } from '@pages/dashboard/financials-loader/financials-loader.component';
import { GeneralOverviewInsights } from '@pages/dashboard/portfolio/general-overview/insights/insights.component';
import { PortfolioDetailsPage } from '@pages/dashboard/portfolio/general-overview/portfolio-details/portfolio-details.component';
import { RugLoaderComponent } from '@pages/dashboard/rug-loader/rug-loader.component';
import { ShellAdminLoaderComponent } from '@pages/dashboard/shell-admin-loader/shell-admin-loader.component';
import { HomePageComponent } from '@pages/home/home.component';
import { TwoFAPage } from '@pages/welcome/2FA/two-FA.component';
import { ChangePasswordPage } from '@pages/welcome/change-password/change-password.component';
import { ErrorPage } from '@pages/welcome/error/error.component';
import { ForgotPasswordPage } from '@pages/welcome/forgot-password/forgot-password.component';
import { LoginPage } from '@pages/welcome/login/login.component';
import { NotFoundPage } from '@pages/welcome/not-found/not-found.component';
import { SignupPage } from '@pages/welcome/signup/signup.component';

export const routes: Routes = [
  {
    path: '',
    component: WelcomeLayout,
    title: 'CXC',
    canActivate: [unloggedGuard],
    children: [
      {
        path: '',
        component: LoginPage,
      },
      {
        path: 'login',
        component: LoginPage,
      },
      {
        path: '2FA',
        component: TwoFAPage,
        canActivate: [appConfigGuard],
      },
      {
        path: 'forgot-password',
        component: ForgotPasswordPage,
      },
      {
        // MISSING GUARD
        path: 'change-password',
        component: ChangePasswordPage,
      },
      {
        path: 'signup',
        component: SignupPage,
      },
      {
        path: 'error',
        component: ErrorPage,
      },
    ],
  },
  {
    path: 'dashboard',
    component: DashboardLayout,
    canActivate: [loggedGuard, moduleGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        /**
         * This component is a hack.
         * We need the RedirectFunction released in angular v20: https://angular.dev/api/router/RedirectFunction
         */
        component: HomeRedirectComponent,
      },
      {
        path: 'portfolio',
        children: [
          {
            path: '',
            pathMatch: 'full',
            /**
             * This component is a hack.
             * We need the RedirectFunction released in angular v20: https://angular.dev/api/router/RedirectFunction
             */
            component: HomeRedirectComponent,
          },
          {
            path: 'overview',
            title: 'CXC - General overview',
            loadComponent: () =>
              import(
                './pages/dashboard/portfolio/general-overview/general-overview.component'
              ).then(m => m.GeneralOverviewPage),
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'insights',
              },
              {
                path: 'insights',
                component: GeneralOverviewInsights,
                title: 'Insights',
              },
              {
                path: 'portfolio-details',
                component: PortfolioDetailsPage,
                title: 'Portfolio details',
              },
            ],
          },
          {
            path: 'facilities',
            title: 'CXC - Facilities',
            loadComponent: () =>
              import(
                './pages/dashboard/portfolio/operations/operations.component'
              ).then(m => m.OperationsPage),
          },
          {
            path: 'facility',
            data: {
              drawer: true,
              breadcrumb: {
                label: 'Facilities',
                url: '/dashboard/portfolio/facilities',
              },
            },

            children: [
              {
                path: ':operationId',
                data: {
                  breadcrumb: { label: '{operation.alias}' },
                },
                canActivate: [operationGuard],
                children: [
                  {
                    path: '',
                    pathMatch: 'full',
                    redirectTo: 'insights',
                  },
                  {
                    path: 'insights',
                    title: 'Insights',

                    data: {
                      icon: 'phosphorChartBar',
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/insights/insights.component'
                      ).then(m => m.InsightsPage),
                  },
                  {
                    path: 'concentrations',
                    title: 'Concentrations',
                    data: {
                      icon: 'phosphorChartDonut',
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/concentrations/concentrations.component'
                      ).then(m => m.ConcentrationsPage),
                  },
                  {
                    path: 'cash-flow',
                    title: 'Cash Flow',
                    data: {
                      icon: 'phosphorChartLine',
                      isNew: true,
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/cash-flow/cash-flow.component'
                      ).then(m => m.CashFlowLayout),
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'collection-chart',
                      },
                      {
                        path: 'collection-chart',
                        title: 'Collection Chart',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/cash-flow/collection-chart/collection-chart.component'
                          ).then(m => m.CashFlowCollectionChartPage),
                      },
                      {
                        path: 'payment-history',
                        title: 'Payment History',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/cash-flow/payment-history/payment-history.component'
                          ).then(m => m.CashFlowPaymentHistoryPage),
                      },
                      {
                        path: 'account-balance',
                        title: 'Account Balance',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/cash-flow/account-balance/account-balance.component'
                          ).then(m => m.CashFlowAccountBalancePage),
                      },
                      {
                        path: 'waterfall',
                        title: 'Waterfall',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/cash-flow/waterfall/waterfall.component'
                          ).then(m => m.CashFlowWaterfallPage),
                      },
                    ],
                  },
                  {
                    path: 'general-information',
                    title: 'General information',
                    data: {
                      icon: 'phosphorFileText',
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/general-information/general-information.component'
                      ).then(m => m.GeneralInformationLayout),
                    children: [
                      {
                        path: '',
                        pathMatch: 'full',
                        redirectTo: 'overview',
                      },
                      {
                        path: 'overview',
                        title: 'Operation Overview',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/general-information/overview/overview.component'
                          ).then(m => m.GeneralInformationOverviewPage),
                      },
                      {
                        path: 'contracts',
                        title: 'Contracts',
                        loadComponent: () =>
                          import(
                            './pages/dashboard/portfolio/operation/general-information/contracts/contracts.component'
                          ).then(m => m.GeneralInformationContractsPage),
                      },
                    ],
                  },
                  {
                    path: 'reports',
                    title: 'Report repository',
                    data: {
                      icon: 'phosphorFolder',
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/reports/reports.component'
                      ).then(m => m.ReportsPage),
                  },
                  {
                    path: 'directory',
                    title: 'Directory',
                    data: {
                      icon: 'phosphorAddressBook',
                    },
                    loadComponent: () =>
                      import(
                        './pages/dashboard/portfolio/operation/directory/directory.component'
                      ).then(m => m.DirectoryPage),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'rug',
        component: RugLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - Rug',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_RUG}/remoteEntry.js`,
                exposedModule: './RugModule',
              }).then(m => m.RugModule),
          },
        ],
      },
      {
        // Shell page nested in the AIVA menu. Declared before 'aiva' so it is
        // matched here instead of being handed to the AIVA remote.
        // Derived from ROUTE_PATHS so the menu and the route cannot drift.
        path: ROUTE_PATHS.SELF_SERVICE_TOOL.slice(
          `${ROUTE_PATHS.DASHBOARD}/`.length
        ),
        title: 'CXC - Self Service Tool',
        loadComponent: () =>
          import(
            './pages/dashboard/self-service-tool/docflow/docflow.component'
          ).then(m => m.DocflowPage),
      },
      {
        path: 'aiva',
        component: RugLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - AIVA',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_AIVA}/remoteEntry.js`,
                exposedModule: './Module',
              }).then(m => m.AivaModule),
          },
        ],
      },
      {
        path: 'companies',
        component: CompaniesLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - Companies',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_COMPANY}/remoteEntry.js`,
                exposedModule: './CompaniesModule',
              }).then(m => m.MFEModule),
          },
        ],
      },
      {
        path: 'financials',
        component: FinancialsLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - Financials',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_FINANCIAL}/remoteEntry.js`,
                exposedModule: './FinancialsModule',
              }).then(m => m.MFEModule),
          },
        ],
      },
      {
        path: 'data-studio',
        component: DataStudioLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - Data Studio',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_DATA_STUDIO}/remoteEntry.js`,
                exposedModule: './DataStudioModule',
              }).then(m => m.MFEModule),
          },
        ],
      },
      {
        path: 'admin',
        component: ShellAdminLoaderComponent,
        children: [
          {
            path: '',
            title: 'CXC - Admin',
            loadChildren: () =>
              loadRemoteModule({
                type: 'module',
                remoteEntry: `${environment.REMOTE_URL_SHELL_ADMIN}/remoteEntry.js`,
                exposedModule: './AdminModule',
              }).then(m => m.AdminModule),
          },
        ],
      },
      {
        path: 'not-found',
        component: NotFoundPage,
      },
      {
        path: '**',
        redirectTo: 'not-found',
      },
    ],
  },
  {
    path: 'account',
    component: DashboardLayout,
    canActivate: [loggedGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: '/account/profile',
      },
      {
        path: 'profile',
        title: 'CXC - Profile',
        component: ProfilePage,
        data: {
          icon: 'phosphorUser',
          drawer: true,
        },
      },
    ],
  },
  {
    path: 'home',
    component: DashboardLayout,
    canActivate: [loggedGuard],
    children: [
      {
        path: '',
        title: 'CXC - Home',
        component: HomePageComponent,
        data: {
          icon: 'phosphorUser',
          drawer: true,
        },
      },
    ],
  },
];
